import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ROLES = ['pending', 'cad', 'records', 'gis', 'upload', 'admin'];
const EMOJI_OPTIONS = ['🎉', '🎂', '🦃', '🎄', '🎃', '🏖️', '🍻', '🚀', '🏆', '❤️'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'cad' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Break Room management
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', event_date: '', emoji: '🎉' });
  const [celebs, setCelebs] = useState([]);
  const [newCeleb, setNewCeleb] = useState({ person_name: '', type: 'birthday', date: '' });

  useEffect(() => {
    fetchUsers();
    fetchEvents();
    fetchCelebs();
  }, []);

  async function fetchUsers() {
    try {
      const { data } = await api.get('/auth/users-list');
      setUsers(data);
    } catch {}
  }

  async function fetchEvents() {
    try { const { data } = await api.get('/breakroom/events'); setEvents(data); } catch {}
  }
  async function fetchCelebs() {
    try { const { data } = await api.get('/breakroom/celebrations'); setCelebs(data); } catch {}
  }

  async function addEvent(e) {
    e.preventDefault();
    if (!newEvent.title || !newEvent.event_date) return;
    try {
      await api.post('/breakroom/events', newEvent);
      setNewEvent({ title: '', event_date: '', emoji: '🎉' });
      fetchEvents();
    } catch { alert('Failed to add event'); }
  }
  async function deleteEvent(id) {
    try { await api.delete(`/breakroom/events/${id}`); fetchEvents(); } catch {}
  }

  async function addCeleb(e) {
    e.preventDefault();
    if (!newCeleb.person_name || !newCeleb.date) return;
    const [y, m, d] = newCeleb.date.split('-').map(Number);
    try {
      await api.post('/breakroom/celebrations', {
        person_name: newCeleb.person_name,
        type: newCeleb.type,
        event_month: m,
        event_day: d,
        start_year: newCeleb.type === 'anniversary' ? y : null,
      });
      setNewCeleb({ person_name: '', type: 'birthday', date: '' });
      fetchCelebs();
    } catch { alert('Failed to add'); }
  }
  async function deleteCeleb(id) {
    try { await api.delete(`/breakroom/celebrations/${id}`); fetchCelebs(); } catch {}
  }

  async function handleRoleChange(userId, role) {
    try {
      await api.patch(`/auth/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    } catch {
      alert('Failed to update role');
    }
  }

  async function handleDeleteUser(u) {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This removes their account permanently. Their job/upload history is preserved but un-attributed.`)) {
      return;
    }
    setDeletingId(u.id);
    try {
      await api.delete(`/auth/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      await api.post('/auth/users', newUser);
      setNewUser({ name: '', email: '', password: '', role: 'upload' });
      fetchUsers();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-10">
        <h1 className="text-2xl font-black text-white tracking-widest uppercase">Admin</h1>

        {/* Users */}
        <div>
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">
            Users
            {users.filter(u => u.role === 'pending').length > 0 && (
              <span className="ml-2 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-xs px-2 py-0.5 rounded">
                {users.filter(u => u.role === 'pending').length} awaiting role
              </span>
            )}
          </h2>
          <div className="space-y-2">
            {[...users].sort((a, b) => (a.role === 'pending' ? -1 : 0) - (b.role === 'pending' ? -1 : 0)).map(u => (
              <div
                key={u.id}
                className="border rounded-lg px-4 py-3 flex items-center justify-between gap-4"
                style={{ backgroundColor: '#0d1b2e', borderColor: u.role === 'pending' ? '#c9a84c' : '#1e3a5f' }}
              >
                <div>
                  <p className="text-white text-sm font-semibold">
                    {u.name}
                    {u.is_sso && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-blue-400 bg-blue-500/10 border border-blue-500/30 px-1.5 py-0.5 rounded">SSO</span>}
                    {u.role === 'pending' && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-1.5 py-0.5 rounded">Needs Role</span>}
                  </p>
                  <p className="text-gray-400 text-xs">{u.email}</p>
                  <p className="text-xs mt-0.5" style={{ color: lastSeenColor(u.last_login_at) }}>
                    {lastSeen(u.last_login_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={u.role}
                    onChange={e => handleRoleChange(u.id, e.target.value)}
                    className="text-xs rounded px-2 py-1 border focus:outline-none"
                    style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#c9a84c' }}
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {!u.is_sso && u.id !== currentUser?.id && (
                    <button
                      onClick={() => handleDeleteUser(u)}
                      disabled={deletingId === u.id}
                      title="Delete user"
                      className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 transition disabled:opacity-50"
                    >
                      {deletingId === u.id ? '…' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Create user manually */}
        <div>
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">Create User</h2>
          <div className="border rounded-lg p-5" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
            <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-3">
              <input required placeholder="Name" value={newUser.name}
                onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                className="rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none"
                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
              <input required type="email" placeholder="Email" value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                className="rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none"
                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
              <input required type="password" placeholder="Password" value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                className="rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none"
                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
              <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                className="rounded px-3 py-2 text-sm border focus:outline-none"
                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#c9a84c' }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {createError && <p className="col-span-2 text-red-400 text-xs">{createError}</p>}
              <button type="submit" disabled={creating}
                className="col-span-2 py-2 rounded text-sm font-bold uppercase tracking-widest disabled:opacity-50 transition"
                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>
                {creating ? 'Creating…' : 'Create User'}
              </button>
            </form>
          </div>
        </div>

        {/* Break Room management */}
        <div>
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">Break Room</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Countdown events */}
            <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
              <h3 className="text-white text-sm font-bold mb-3">⏳ Countdown Events</h3>
              <form onSubmit={addEvent} className="space-y-2 mb-3">
                <input placeholder="Event name (e.g. Summer Party)" value={newEvent.title}
                  onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 border focus:outline-none"
                  style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
                <div className="flex gap-2">
                  <input type="date" value={newEvent.event_date}
                    onChange={e => setNewEvent({ ...newEvent, event_date: e.target.value })}
                    className="flex-1 rounded px-3 py-1.5 text-sm text-white border focus:outline-none"
                    style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
                  <select value={newEvent.emoji} onChange={e => setNewEvent({ ...newEvent, emoji: e.target.value })}
                    className="rounded px-2 py-1.5 text-sm border focus:outline-none"
                    style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}>
                    {EMOJI_OPTIONS.map(em => <option key={em} value={em}>{em}</option>)}
                  </select>
                  <button type="submit" className="px-4 rounded text-sm font-bold uppercase tracking-wide" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>+ Add</button>
                </div>
              </form>
              <div className="space-y-1">
                {events.length === 0 ? <p className="text-gray-600 text-xs italic">No events yet.</p> :
                  events.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#142236' }}>
                      <span className="text-gray-200">{ev.emoji} {ev.title} <span className="text-gray-500 text-xs">· {new Date(ev.event_date).toLocaleDateString()}</span></span>
                      <button onClick={() => deleteEvent(ev.id)} className="text-gray-500 hover:text-red-400 text-xs">✕</button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Birthdays & anniversaries */}
            <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
              <h3 className="text-white text-sm font-bold mb-3">🎂 Birthdays & Anniversaries</h3>
              <form onSubmit={addCeleb} className="space-y-2 mb-3">
                <input placeholder="Person's name" value={newCeleb.person_name}
                  onChange={e => setNewCeleb({ ...newCeleb, person_name: e.target.value })}
                  className="w-full rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 border focus:outline-none"
                  style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
                <div className="flex gap-2">
                  <select value={newCeleb.type} onChange={e => setNewCeleb({ ...newCeleb, type: e.target.value })}
                    className="rounded px-2 py-1.5 text-sm border focus:outline-none"
                    style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#c9a84c' }}>
                    <option value="birthday">🎂 Birthday</option>
                    <option value="anniversary">🎉 Work Anniversary</option>
                  </select>
                  <input type="date" value={newCeleb.date}
                    onChange={e => setNewCeleb({ ...newCeleb, date: e.target.value })}
                    title={newCeleb.type === 'anniversary' ? 'Their start date (year is used for years of service)' : 'Birth date'}
                    className="flex-1 rounded px-3 py-1.5 text-sm text-white border focus:outline-none"
                    style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }} />
                  <button type="submit" className="px-4 rounded text-sm font-bold uppercase tracking-wide" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>+ Add</button>
                </div>
                {newCeleb.type === 'anniversary' && <p className="text-gray-500 text-xs">Use their original start date — years of service are calculated from it.</p>}
              </form>
              <div className="space-y-1">
                {celebs.length === 0 ? <p className="text-gray-600 text-xs italic">None yet.</p> :
                  celebs.map(c => (
                    <div key={c.id} className="flex items-center justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#142236' }}>
                      <span className="text-gray-200">{c.type === 'birthday' ? '🎂' : '🎉'} {c.person_name} <span className="text-gray-500 text-xs">· {MONTHS[c.event_month - 1]} {c.event_day}{c.type === 'anniversary' && c.start_year ? ` (${c.start_year})` : ''}</span></span>
                      <button onClick={() => deleteCeleb(c.id)} className="text-gray-500 hover:text-red-400 text-xs">✕</button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// "Last seen" for the user list. Login tracking is newer than some accounts, so
// a blank reads as "not since tracking started", never as "never logged in".
function lastSeen(iso) {
  if (!iso) return 'No sign-in recorded yet';
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  const time = then.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  if (days <= 0) return `Last seen today · ${then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  if (days === 1) return `Last seen yesterday · ${time}`;
  if (days < 30) return `Last seen ${days} days ago · ${time}`;
  return `Last seen ${time}`;
}

function lastSeenColor(iso) {
  if (!iso) return '#4b5563';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 1) return '#4ade80';   // active
  if (days < 30) return '#9ca3af';   // around recently
  return '#6b7280';                  // dormant
}
